import React from 'react';
import styles from '../../styles/formComponent/FormComponent.module.scss';
import InputBox from './InputBox';
import DatePicker from './DatePicker';
import Selectbox from './Selectbox';
import CheckboxComponent from './CheckboxComponent';
import SwitchComponent from './SwitchComponent';
import RadioComponent from './RadioComponent';
import AutoSuggest from './AutoSuggest';
import Content from './Content';

const FormComponent = ({
  type = 'input',
  label,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  variant = 'outlined',
  size = 'medium',
  multiline = false,
  rows = 4,
  options = [],
  checked,
  indeterminate = false,
  description,
  color = 'primary',
  className = '',
  // Content component props
  text,
  children,
  // Validation props
  showValidationIcon = true,
  validationState, // 'valid', 'invalid', 'validating'
  // AutoSuggest specific props
  multiple = false,
  freeSolo = false,
  loading = false,
  noOptionsText = 'No options',
  loadingText = 'Loading...',
  filterOptions,
  getOptionLabel,
  getOptionValue,
  isOptionEqualToValue,
  renderOption,
  showCheckboxes = false,
  clearOnBlur = false,
  clearOnEscape = true,
  disableClearable = false,
  openOnFocus = false,
  // Radio specific props
  direction = 'vertical', // 'vertical' or 'horizontal'
  // Additional props for better form integration
  autoComplete,
  autoFocus = false,
  readOnly = false,
  ...props
}) => {
  const renderComponent = () => {
    switch (type) {
      case 'input':
      case 'text':
      case 'password':
      case 'email':
      case 'number':
        return (
          <InputBox
            type={type === 'input' ? 'text' : type}
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            variant={variant}
            size={size}
            multiline={multiline}
            rows={rows}
            className={className}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            readOnly={readOnly}
            showValidationIcon={showValidationIcon}
            validationState={validationState}
            {...props}
          />
        );
      
      case 'date':
        return (
          <DatePicker
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            variant={variant}
            size={size}
            className={className}
            autoFocus={autoFocus}
            readOnly={readOnly}
            showValidationIcon={showValidationIcon}
            validationState={validationState}
            {...props}
          />
        );
      
      case 'select':
        return (
          <Selectbox
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            variant={variant}
            size={size}
            options={options}
            multiple={multiple}
            allowClear={props.allowClear}
            readOnly={readOnly}
            disableClearable={disableClearable}
            className={className}
            {...props}
          />
        );
      
      case 'checkbox':
        return (
          <CheckboxComponent
            label={label}
            name={name}
            checked={checked}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            size={size}
            indeterminate={indeterminate}
            description={description}
            className={className}
            {...props}
          />
        );
      
      case 'switch':
        return (
          <SwitchComponent
            label={label}
            name={name}
            checked={checked}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            size={size}
            color={color}
            description={description}
            className={className}
            {...props}
          />
        );
      
      case 'radio':
        return (
          <RadioComponent
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            size={size}
            color={color}
            description={description}
            options={options}
            direction={direction}
            className={className}
            {...props}
          />
        );
      
      case 'autocomplete':
      case 'autosuggest':
        return (
          <AutoSuggest
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            variant={variant}
            size={size}
            options={options}
            multiple={multiple}
            freeSolo={freeSolo}
            loading={loading}
            noOptionsText={noOptionsText}
            loadingText={loadingText}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={renderOption}
            showCheckboxes={showCheckboxes}
            clearOnBlur={clearOnBlur}
            clearOnEscape={clearOnEscape}
            disableClearable={disableClearable}
            openOnFocus={openOnFocus}
            className={className}
            {...props}
          />
        );
      
      case 'content':
      case 'contentText':
        return (
          <Content
            variant={props.variant}
            color={props.color}
            align={props.align}
            type={props.contentType || 'text'}
            weight={props.weight}
            spacing={props.spacing}
            className={className}
          >
            {props.text || children || placeholder}
          </Content>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.formWrapper} ${className}`}>
      <div 
        className={`
          ${styles.fieldWrapper} 
          ${required ? styles.required : ''} 
          ${disabled ? styles.disabled : ''}
        `}
      >
        {renderComponent()}
      </div>
    </div>
  );
};

export default FormComponent;